import { useState, useEffect } from 'react';
import api from '../../api';

export default function ReportsManager() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('open'); // 'open', 'resolved', 'all'

  const fetchReports = () => {
    setLoading(true);
    api.get('/api/reports')
      .then(({ data }) => setReports(data))
      .catch((err) => console.error('Error fetching reports:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleToggleStatus = async (report) => {
    const newStatus = report.status === 'open' ? 'resolved' : 'open';
    try {
      await api.patch(`/api/reports/${report.id}`, { status: newStatus });
      setReports((prev) =>
        prev.map((r) => (r.id === report.id ? { ...r, status: newStatus } : r))
      );
    } catch (err) {
      alert('Aanpassen mislukt.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze melding wilt verwijderen?')) return;
    try {
      await api.delete(`/api/reports/${id}`);
      setReports((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      alert('Verwijderen mislukt.');
    }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleString('nl-BE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const filtered = reports.filter((r) => {
    if (filterStatus === 'all') return true;
    return r.status === filterStatus;
  });

  const openCount = reports.filter((r) => r.status === 'open').length;

  return (
    <div>
      <div className="admin-section-header">
        <div>
          <h2 className="admin-section-title">Foutmeldingen</h2>
          <p className="admin-section-sub">
            {openCount} openstaande melding{openCount === 1 ? '' : 'en'} ({reports.length} in totaal)
          </p>
        </div>
      </div>

      <div className="admin-toolbar" style={{ marginBottom: 20, display: 'flex', gap: 10 }}>
        <button
          className={`count-btn ${filterStatus === 'open' ? 'active' : ''}`}
          onClick={() => setFilterStatus('open')}
        >
          Openstaand ({openCount})
        </button>
        <button
          className={`count-btn ${filterStatus === 'resolved' ? 'active' : ''}`}
          onClick={() => setFilterStatus('resolved')}
        >
          Opgelost ({reports.length - openCount})
        </button>
        <button
          className={`count-btn ${filterStatus === 'all' ? 'active' : ''}`}
          onClick={() => setFilterStatus('all')}
        >
          Alle ({reports.length})
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', opacity: 0.6 }}>Meldingen laden...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0' }}>
          Geen meldingen gevonden in deze categorie.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {filtered.map((r) => (
            <div
              key={r.id}
              style={{
                background: '#ffffff',
                border: r.status === 'open' ? '1px solid #fecaca' : '1px solid #e2e8f0',
                borderRadius: 12,
                padding: '18px 20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <span
                    style={{
                      background: r.status === 'open' ? '#fee2e2' : '#dcfce7',
                      color: r.status === 'open' ? '#dc2626' : '#16a34a',
                      padding: '3px 8px',
                      borderRadius: 6,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      marginRight: 10,
                    }}
                  >
                    {r.status === 'open' ? 'Open' : 'Opgelost'}
                  </span>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>
                    Vraag #{r.questionId}
                  </span>
                  {r.userName && (
                    <span style={{ fontSize: '0.85rem', color: '#64748b', marginLeft: 10 }}>
                      gemeld door {r.userName} ({r.userEmail})
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{formatDate(r.createdAt)}</div>
              </div>

              {/* Opmerking student */}
              <div
                style={{
                  background: '#f8fafc',
                  borderLeft: '4px solid #3b82f6',
                  padding: '10px 14px',
                  borderRadius: '0 8px 8px 0',
                  marginBottom: 12,
                  fontSize: '0.95rem',
                  color: '#1e293b',
                }}
              >
                <strong>Melding student:</strong>{' '}
                {r.comment ? r.comment : <em style={{ color: '#94a3b8' }}>(geen toelichting ingevoerd)</em>}
              </div>

              {/* Vraag & antwoord preview */}
              <div style={{ fontSize: '0.85rem', color: '#475569', marginBottom: 12 }}>
                <div style={{ marginBottom: 4 }}>
                  <strong>Vraag:</strong> {r.question}
                </div>
                <div>
                  <strong>Correct antwoord in quiz:</strong>{' '}
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>{r.correctAnswer}</span>
                </div>
              </div>

              {/* Acties */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  className="btn"
                  onClick={() => handleToggleStatus(r)}
                  style={{
                    background: r.status === 'open' ? '#16a34a' : '#64748b',
                    color: '#fff',
                    padding: '6px 14px',
                    fontSize: '0.8rem',
                    borderRadius: 6,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {r.status === 'open' ? '✓ Markeer als opgelost' : '↩ Heropenen'}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => handleDelete(r.id)}
                  style={{
                    background: 'transparent',
                    color: '#dc2626',
                    padding: '6px 12px',
                    fontSize: '0.8rem',
                    borderRadius: 6,
                    border: '1px solid #fecaca',
                    cursor: 'pointer',
                  }}
                >
                  Verwijderen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
